// Shared posts endpoint contracts.
import {readPostId, readPostWriteRequest, readPostCommentRequest, readPostFeedRequest} from '../post-requests.js';
export {PostRequestContractError, readPostId, readPostWriteRequest, readPostCommentRequest, readPostFeedRequest, readPostReactionRequest} from '../post-requests.js';

export const POST_PATHS = {
  exploreFeed: '/api/posts/explore-feed',
  create: '/api/posts/create',
};

export class PostResponseContractError extends Error {
  constructor(operation, detail) {
    super(`Invalid post ${operation} response: ${detail}`);
    this.name = 'PostResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}

const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const supportedReactions = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
const isPostReference = value => typeof value === 'string' && value.length > 0
  || isRecord(value) && typeof value._id === 'string' && Boolean(value._id);
const optionalText = value => value == null || typeof value === 'string';
const optionalDate = value => value === undefined || typeof value === 'string' && Number.isFinite(Date.parse(value));
const isUser = value => isPostReference(value) && (!isRecord(value)
  || ['firstName', 'lastName', 'profilePicture'].every(key => optionalText(value[key]))
    && ['password', 'refreshToken', 'verificationCode', 'deviceTokens', 'email', 'address'].every(key => value[key] === undefined));
const isReactions = value => value === undefined || Array.isArray(value) && value.every(reaction => isRecord(reaction)
  && isUser(reaction.user) && supportedReactions.includes(reaction.emoji));
const isComments = value => value === undefined || Array.isArray(value) && value.every(comment => isRecord(comment)
  && typeof comment._id === 'string' && Boolean(comment._id) && (comment.user === null || isUser(comment.user))
  && typeof comment.comment === 'string' && (comment.parentCommentId == null || typeof comment.parentCommentId === 'string' && Boolean(comment.parentCommentId))
  && optionalDate(comment.createdAt) && isReactions(comment.reactions));
const isPostRecord = post => isRecord(post) && typeof post._id === 'string'
  && Boolean(post._id) && typeof post.content === 'string' && (post.author === null || isUser(post.author)) && isPostReference(post.community)
  && (!isRecord(post.community) || ['name', 'description'].every(key => optionalText(post.community[key])))
  && ['createdAt', 'updatedAt'].every(key => optionalDate(post[key]))
  && ['media', 'mediaThumbnails', 'tags'].every(key => post[key] === undefined || Array.isArray(post[key]) && post[key].every(value => typeof value === 'string'))
  && ['isEdited', 'isFlagged'].every(key => post[key] === undefined || typeof post[key] === 'boolean')
  && ['likes', 'flaggedBy'].every(key => post[key] === undefined || Array.isArray(post[key]) && post[key].every(isUser))
  && (post.commentCount === undefined || Number.isSafeInteger(post.commentCount) && post.commentCount >= 0)
  && isComments(post.comments) && isReactions(post.reactions);

export const readPostResponse = (payload, expected = {}) => {
  if (!isRecord(payload) || payload.success !== true || !isPostRecord(payload.data) || expected.id && payload.data._id.toLowerCase() !== expected.id.toLowerCase()) {
    throw new PostResponseContractError('record', 'expected a success envelope containing one post record.');
  }
  return payload;
};

export const readPostFeedResponse = (payload, { requirePagination = false } = {}) => {
  const pagination = payload?.pagination;
  if (!isRecord(payload) || payload.success !== true || !Array.isArray(payload.data)
    || !payload.data.every(isPostRecord)
    || (requirePagination || pagination !== undefined) && (!isRecord(pagination)
      || typeof pagination.hasMore !== 'boolean'
      || (pagination.authorId !== undefined && (typeof pagination.authorId !== 'string' || !/^[a-f0-9]{24}$/i.test(pagination.authorId)))
      || (pagination.nextCursor !== null && typeof pagination.nextCursor !== 'string')
      || (pagination.hasMore && (!pagination.nextCursor || payload.data.length === 0)))) {
    throw new PostResponseContractError('feed', 'expected a success envelope containing post records and valid optional pagination.');
  }
  return payload;
};

export const readPostDeletionResponse = payload => {
  if (!isRecord(payload) || payload.success !== true || payload.message !== 'Post deleted successfully') {
    throw new PostResponseContractError('deletion', 'expected the post deletion acknowledgement.');
  }
  return payload;
};

export const readPostReactionResponse = (payload, kind = 'post', expected = {}) => {
  const data = payload?.data;
  const idField = kind === 'comment' ? 'commentId' : 'postId';
  if (!isRecord(payload) || payload.success !== true || !isRecord(data)
    || typeof data[idField] !== 'string' || data[idField].length === 0
    || expected.id && data[idField].toLowerCase() !== expected.id.toLowerCase()
    || !Array.isArray(data.reactions)
    || !data.reactions.every(reaction => isRecord(reaction)
      && typeof reaction.user === 'string' && reaction.user.length > 0
      && supportedReactions.includes(reaction.emoji))) {
    throw new PostResponseContractError(`${kind} reaction`, `expected ${idField} and the persisted reaction list.`);
  }
  return payload;
};

const enc = (value) => encodeURIComponent(value);

export const buildPostPath = (postId) => `/api/posts/${enc(readPostId(postId))}`;
export const buildPostCommunityFeedPath = (communityId) =>
  `/api/posts/community-feed/${enc(readPostId(communityId))}`;
export const buildPostActionPath = (postId, action) =>
  `${buildPostPath(postId)}/${action}`;

export const createPostsClient = (adapter) => {
  if (!adapter || typeof adapter !== 'object') {
    throw new TypeError('createPostsClient requires a transport adapter.');
  }

  return {
    getExploreFeed(options = {}) {
      if (options.params !== undefined) readPostFeedRequest(options.params);
      return adapter.get(POST_PATHS.exploreFeed, options);
    },
    getCommunityFeed(communityId, options = {}) {
      if (options.params !== undefined) readPostFeedRequest(options.params);
      return adapter.get(buildPostCommunityFeedPath(readPostId(communityId)), options);
    },
    getById(postId, options = {}) {
      return adapter.get(buildPostPath(readPostId(postId)), options);
    },
    create(payload, options = {}) {
      return adapter.post(POST_PATHS.create, readPostWriteRequest(payload), options);
    },
    like(postId, payload = null, options = {}) {
      return adapter.post(buildPostActionPath(readPostId(postId), 'like'), payload, options);
    },
    comment(postId, payload, options = {}) {
      return adapter.post(buildPostActionPath(readPostId(postId), 'comment'), readPostCommentRequest(payload), options);
    },
    flag(postId, options = {}) {
      return adapter.patch(buildPostActionPath(readPostId(postId), 'flag'), null, options);
    },
    update(postId, payload, options = {}) {
      return adapter.patch(buildPostActionPath(readPostId(postId), 'update'), readPostWriteRequest(payload, true), options);
    },
    remove(postId, options = {}) {
      return adapter.delete(buildPostPath(readPostId(postId)), options);
    },
  };
};
